import { useEffect, useState, useCallback } from 'react';
import RetailerLayout from './RetailerLayout';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Webhook, ShieldCheck, Send, Save, Activity, BookOpen, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { retailer, getErrorMessage } from '../../utils/api';

const _DELIVERY_ICON = {
  delivered: <CheckCircle2 className="w-4 h-4 text-emerald-600" />,
  pending_retry: <Clock className="w-4 h-4 text-amber-500" />,
  dead: <XCircle className="w-4 h-4 text-red-600" />,
};

export default function RetailerWebhookConfig({ user, onLogout }) {
  const [config, setConfig] = useState(null);
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [pushEnabled, setPushEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [showDocs, setShowDocs] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await retailer.getWebhookConfig();
      const c = res.data.config;
      setConfig(c);
      setUrl(c.webhook_base_url || '');
      setPushEnabled(!!c.push_enabled);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load webhook config'));
    }
    try {
      const dres = await retailer.getWebhookDeliveries();
      setDeliveries(dres.data.deliveries || []);
    } catch (e) { /* non-fatal */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      setSaving(true);
      const payload = { webhook_base_url: url, push_enabled: pushEnabled };
      if (secret.trim()) payload.hmac_secret = secret.trim();
      const res = await retailer.setWebhookConfig(payload);
      setConfig(res.data.config);
      setSecret('');
      toast.success('Webhook configuration saved');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to save'));
    } finally { setSaving(false); }
  };

  const test = async () => {
    try {
      setTesting(true);
      const res = await retailer.testWebhook();
      if (res.data.pushed) toast.success('Webhook verified — test ping delivered');
      else toast.error(`Test failed: ${res.data.reason || 'no response'}`);
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Test failed'));
    } finally { setTesting(false); }
  };

  return (
    <RetailerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="retailer-webhook-page">
        <div className="bg-gradient-to-r from-slate-900 to-teal-900 rounded-2xl p-8 text-white shadow-xl">
          <h1 className="text-3xl font-bold flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            <Webhook className="w-7 h-7" /> Push Delivery
          </h1>
          <p className="text-teal-100 mt-1 text-sm max-w-2xl">
            Receive signed order, refund, and reconciliation events straight to your systems.
            DealShaq signs every payload with HMAC-SHA256 using your shared secret
            (<code>X-DealShaq-Signature</code>).
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              Webhook Endpoint
              {config?.verified && <Badge className="bg-emerald-100 text-emerald-800"><ShieldCheck className="w-3 h-3 mr-1" />Verified</Badge>}
            </CardTitle>
            <CardDescription>
              We POST to <code>{'{base URL}'}/order</code>, <code>/refund</code>, and <code>/reconciliation</code>.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <Label className="text-xs">Webhook Base URL</Label>
              <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://api.yourstore.com/dealshaq" data-testid="webhook-url-input" />
            </div>
            <div>
              <Label className="text-xs">HMAC Shared Secret {config?.has_secret && <span className="text-emerald-600">(set — leave blank to keep)</span>}</Label>
              <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={config?.has_secret ? '••••••••' : 'enter a strong secret'} data-testid="webhook-secret-input" />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium">Enable Push Delivery</p>
                <p className="text-xs text-gray-500">Until enabled, DealShaq returns <code>no_webhook_configured</code>.</p>
              </div>
              <Switch checked={pushEnabled} onCheckedChange={setPushEnabled} data-testid="webhook-enable-switch" />
            </div>
            <div className="flex gap-2">
              <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="webhook-save-btn">
                <Save className="w-4 h-4 mr-2" />{saving ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="outline" onClick={test} disabled={testing || !config?.has_secret || !config?.webhook_base_url} data-testid="webhook-test-btn">
                <Send className="w-4 h-4 mr-2" />{testing ? 'Testing…' : 'Send Test Ping'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Activity className="w-4 h-4" /> Recent Deliveries</CardTitle>
            <CardDescription>Last outbound webhook attempts. Failed events retry automatically with backoff.</CardDescription>
          </CardHeader>
          <CardContent>
            {deliveries.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center" data-testid="webhook-deliveries-empty">No delivery attempts yet.</p>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Event</TableHead><TableHead>Status</TableHead>
                  <TableHead>HTTP</TableHead><TableHead>Attempts</TableHead><TableHead>When</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {deliveries.map((d, i) => (
                    <TableRow key={d.id || `dlv-${i}`} data-testid={`webhook-delivery-${d.id || i}`}>
                      <TableCell className="capitalize">{d.event_type}</TableCell>
                      <TableCell><span className="flex items-center gap-1">{_DELIVERY_ICON[d.status] || null}{d.status || 'unknown'}</span></TableCell>
                      <TableCell>{d.status_code ?? d.error?.slice(0, 24) ?? '—'}</TableCell>
                      <TableCell>{d.attempts}/{d.max_attempts}</TableCell>
                      <TableCell className="text-xs text-gray-500">{new Date(d.created_at).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="cursor-pointer" onClick={() => setShowDocs((v) => !v)}>
            <CardTitle className="text-base flex items-center gap-2"><BookOpen className="w-4 h-4" /> DRLP API Reference {showDocs ? '▾' : '▸'}</CardTitle>
            <CardDescription>Endpoints, payloads, and signature verification for your integration.</CardDescription>
          </CardHeader>
          {showDocs && (
            <CardContent className="space-y-4 text-sm" data-testid="drlp-api-docs">
              <div>
                <p className="font-semibold mb-1">Ingestion (you → DealShaq)</p>
                <p className="text-gray-600">Authenticate with header <code>X-DRLP-API-Key: &lt;your key&gt;</code> (issued by DealShaq admin).</p>
                <ul className="list-disc ml-6 text-gray-700 mt-1">
                  <li><code>POST /api/drlp/catalog/update</code> — sku, item_name, tax_category, is_taxable, is_snap_eligible, age_restriction_flag</li>
                  <li><code>POST /api/drlp/inventory/update</code> — sku, inventory_available</li>
                  <li><code>POST /api/drlp/pricing/update</code> — sku, price</li>
                  <li><code>POST /api/drlp/store-metadata/update</code> — store_name, store_address, store_zip, store_locality</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-1">Push (DealShaq → you)</p>
                <p className="text-gray-600">We POST signed JSON to <code>{'{your base URL}'}/order</code>, <code>/refund</code>, <code>/reconciliation</code> with header <code>X-DealShaq-Signature: sha256=&lt;hex&gt;</code>.</p>
              </div>
              <div>
                <p className="font-semibold mb-1">Verify the signature (Node.js)</p>
                <pre className="bg-gray-900 text-emerald-300 text-xs rounded-lg p-3 overflow-x-auto">{`const crypto = require('crypto');
const sig = req.headers['x-dealshaq-signature'].replace('sha256=', '');
const expected = crypto.createHmac('sha256', SHARED_SECRET)
  .update(rawBody)               // the exact raw request bytes
  .digest('hex');
const ok = crypto.timingSafeEqual(Buffer.from(sig), Buffer.from(expected));`}</pre>
              </div>
              <div>
                <p className="font-semibold mb-1">Sample order payload</p>
                <pre className="bg-gray-50 border text-xs rounded-lg p-3 overflow-x-auto">{`{
  "order_id": "DS-2026-08-001234",
  "timestamp": "2026-08-12T07:44:00-04:00",
  "store_id": "WFM-RESTON-001",
  "items": [{ "sku": "123456789", "quantity": 2, "unit_price": 0.79,
    "extended_price": 1.58, "tax_category": "Food", "tax_rate": 0.01, "tax_amount": 0.04 }],
  "order_subtotal": 1.58, "order_tax_total": 0.04, "order_total": 1.62
}`}</pre>
              </div>
            </CardContent>
          )}
        </Card>
      </div>
    </RetailerLayout>
  );
}
